import Select, { StylesConfig } from 'react-select';

export type TDropdownOption = {
  label: string,
  value: string,
} | undefined;

export type TDropdownOptions = TDropdownOption[];

interface IDropdownProps {
  onChange?: (newValue: { value: string; label: string; }) => void,
  options: TDropdownOptions,
  value: TDropdownOption,
  placeholder?: string,
  id: string,
  isMulti?: boolean,
  isSearchable?: boolean,
  errorMessage?: string,
  showErrorMessage?: boolean,
  label?: string,
  className?: string,
  closeMenuOnSelect?: boolean,
}

export default function Dropdown({
  options,
  value,
  placeholder,
  onChange,
  id,
  isMulti,
  isSearchable = false,
  errorMessage,
  showErrorMessage,
  label,
  className,
  closeMenuOnSelect = true,
}: IDropdownProps) {
  const emitOnChange = onChange;
  // Custom styling for react-select, see: https://react-select.com/styles
  const styles: StylesConfig = {
    control: (provided, state) => ({
      'display': 'flex',
      'alignItems': 'center',
      'backgroundColor': 'white',
      'minHeight': '48px',
      'borderRadius': '2rem',
      'border': '1px solid #E5E7EB',
      'cursor': 'pointer',
      'color': '#00202D',
      '&:hover': { 'borderColor': '#99D9F2' },
      '&:focus': { 'borderColor': '#99D9F2' },
      'fontSize': '1rem',
      ...state.menuIsOpen && { 'borderBottomLeftRadius': '0', 'borderBottomRightRadius': '0' },
    }),
    valueContainer: () => ({
      'display': 'flex',
      'alignItems': 'center',
      'paddingLeft': '24px',
      'marginRight': 'auto',
      'marginLeft': 'auto',
      'paddingTop': '4px',
      'paddingBottom': '4px',
      ...isMulti && { 'maxWidth': '90%', 'flexWrap': 'wrap', '& > input': { 'position': 'fixed' } },
    }),
    singleValue: () => ({
      'position': 'relative',
      'fontSize': '1rem',
    }),
    indicatorsContainer: () => ({
      'display': 'flex',
      'alignItems': 'center',
      'paddingRight': '16px',
    }),
    dropdownIndicator: () => ({
      'display': 'flex',
    }),
    menu: () => ({
      'position': 'absolute',
      'paddingTop': '8px',
      'paddingBottom': '8px',
      'backgroundColor': 'white',
      'width': '100%',
      'boxShadow': '0px 4px 4px rgba(0, 0, 0, 0.1)',
      'borderBottomLeftRadius': '2rem',
      'borderBottomRightRadius': '2rem',
      'overflow': 'hidden',
      'border': '1px solid #E5E7EB',
      'borderTop': '0',
    }),
    menuList: () => ({
      'maxHeight': '280px',
      'overflowY': 'auto',
      'overflowX': 'hidden',
    }),
    option: (provided, state) => ({
      'padding': '8px 24px',
      'backgroundColor': 'white',
      '&:focus': { 'backgroundColor': '#E5F5FC' },
      '&:hover': { 'backgroundColor': '#E5F5FC' },
      'fontSize': '1rem',
      'cursor': 'pointer',
      ...state.isSelected && { 'color': '#009FDF', 'fontWeight': '600' },
    }),
    placeholder: () => ({
      'fontSize': '1rem',
      'color': '#5B5E60',
    }),
    multiValue: (provided) => ({
      ...provided,
      'border': '1px solid #D5D6D8',
      'backgroundColor': '#E5F5FC',
      'borderRadius': '2rem',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      '&:hover': { 'backgroundColor': '#FAD6DC', 'color': '#E73252' },
      margin: '4px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
    }),
  };

  return (
    <div className={ className }>
      { label &&
        <div className='flex items-center mb-1'>
          <label htmlFor={ `react-select-${ id }-input` } className='text-body-md text-body-md--semibold' data-error={ showErrorMessage && errorMessage ? 'true' : 'false' }>{ label }</label>
        </div>
      }
      <Select
        key={ id }
        id={ id }
        isMulti={ isMulti }
        closeMenuOnSelect={ closeMenuOnSelect }
        instanceId={ id }
        options={ options }
        onChange={ emitOnChange }
        value={ value }
        classNamePrefix={ 'react-select' }
        isSearchable={ isSearchable }
        placeholder={ placeholder ?? null }
        components={ { IndicatorSeparator: () => null } }
        styles={ styles }
        aria-label={ id }
        isClearable={ true }
      />
      {
        errorMessage && errorMessage.length > 0 && showErrorMessage &&
        <span className='block w-full text-body-sm mt-2 text-error md:text-left'>{errorMessage}</span>
      }
    </div>
  );
}
